import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { pull } from "@aprao/common/src/array/pull.js";
import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import { sumKey } from "@aprao/common/src/math/sum.js";
import type {
  CountableNumber,
  FundingType,
} from "@aprao/common/src/shapes/common.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable, toJS } from "mobx";
import { computedFn } from "mobx-utils";
import { mapForNullable } from "option-t/nullable";

import type {
  ShapeOtherIncomeGroup,
  ShapeOtherIncomeItem,
} from "../../shapes/ShapeRevenueGroup.js";
import { WithProjectionPeriodsDelegate } from "../delegates/WithProjectionPeriodsDelegate.js";
import { OtherIncomeItemFacade } from "./OtherIncomeItemFacade.js";
import { RevenueGroupBaseFacade } from "./RevenueGroupBaseFacade.js";

export class OtherIncomeGroupFacade {
  readonly category = "other";

  readonly $model;

  readonly _revenueGroupBase;

  constructor(model: ShapeOtherIncomeGroup) {
    makeAutoObservable(this);

    this.$model = model;
    this._revenueGroupBase = new RevenueGroupBaseFacade(this.$model);
  }

  get shouldIncludeInGdv() {
    return this.$model.shouldIncludeInGdv;
  }

  readonly _decorateItems = computedFn(
    (item: ShapeOtherIncomeItem) => new OtherIncomeItemFacade(item),
  );

  get items() {
    return inferReadonlyArray(
      this.$model.items.map((item) => this._decorateItems(item)),
    );
  }

  get projectionPeriods() {
    return new WithProjectionPeriodsDelegate(this.$model);
  }

  get projectionDuration() {
    return this.projectionPeriods.duration;
  }

  getGrossProceedsProjectionAtPeriodIndex = (periodIndex: number) =>
    (this.valueGross ?? FRACTION_ZERO).mul(
      this.projectionPeriods.getPortionAtPeriodIndex(periodIndex) ??
        FRACTION_ZERO,
    );

  get totalCount() {
    return this.items
      .map(({ quantity }) => quantity)
      .reduce((prev, curr) => prev + curr, 0);
  }

  get valueGross() {
    return mapForNullable(sumKey(this.items, "aggregateValue"), (value) =>
      value.mul(this.valueGrossScale),
    );
  }

  get averageValue() {
    return this.valueGross?.div(this.totalCount) ?? null;
  }

  set shouldIncludeInGdv(shouldInclude: boolean) {
    this.$model.shouldIncludeInGdv = shouldInclude;
  }

  addItem(params: {
    count: CountableNumber;
    description: string;
    value: BigFraction | null;
  }) {
    this.$model.items.push(
      toJS({
        quantity: params.count,
        description: params.description,
        value: params.value?.toFraction() ?? null,
      }),
    );
  }

  removeItem(item: OtherIncomeItemFacade) {
    pull(this.$model.items, item.$model);
  }

  breakdownItem(item: OtherIncomeItemFacade) {
    this.removeItem(item);
    times(item.quantity, () => {
      this.addItem(
        toJS({
          count: 1 as CountableNumber,
          description: item.description,
          value: item.value,
        }),
      );
    });
  }

  // below are all the delegates
  get name() {
    return this._revenueGroupBase.name;
  }

  get fundingType() {
    return this._revenueGroupBase.fundingType;
  }

  get valueGrossScale() {
    return this._revenueGroupBase.valueGrossScale;
  }

  set name(name: string) {
    this._revenueGroupBase.name = name;
  }

  set fundingType(fundingType: FundingType) {
    this._revenueGroupBase.fundingType = fundingType;
  }

  set valueGrossScale(ratio: BigFraction) {
    this._revenueGroupBase.valueGrossScale = ratio;
  }
}
